import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef } from "react";

import Button from "../../mantine/button/Button";
import styles from "./UploadImage.module.scss";

const ALLOWED_FILE_TYPES = [
  "image/png",
  "image/gif",
  "image/jpg",
  "image/jpeg",
  "image/webp",
];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

type Props = {
  disabled?: boolean;
  /** Absolute URL for preview (or null/undefined - then a placeholder) */
  valueUrl?: string | null;
  placeholderLetter?: string;
  /** Shown instead of the letter when there is no image. */
  placeholderImageUrl?: string;
  /** MIME types to accept; defaults to PNG, GIF, JPG, JPEG and WebP. */
  allowedTypes?: string[];
  onUpload: (file: File) => void;
  onRemove: () => void;
};

const fileTypeLabel = (type: string) =>
  type.replace("image/", "").toUpperCase().replace("WEBP", "WebP");

export default function UploadImage({
  disabled,
  valueUrl,
  placeholderLetter = "I",
  placeholderImageUrl,
  allowedTypes = ALLOWED_FILE_TYPES,
  onUpload,
  onRemove,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // if parent resets preview -> clean file input
  useEffect(() => {
    if (!valueUrl && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [valueUrl]);

  const validate = (file: File) => {
    if (!allowedTypes.includes(file.type)) {
      const labels = allowedTypes.map(fileTypeLabel);
      const list = `${labels.slice(0, -1).join(", ")}, or ${labels.at(-1)}`;

      enqueueSnackbar(`Invalid file type. Please upload ${list} images only.`, {
        variant: "error",
        persist: true,
      });

      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar(
        "File size exceeds 5MB limit. Please upload a smaller image.",
        { variant: "error", persist: true },
      );

      return false;
    }

    return true;
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validate(file)) {
      if (fileInputRef.current) fileInputRef.current.value = "";

      return;
    }
    onUpload(file); // parent creates preview URL and sets valueUrl
  };

  const handleRemove = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    onRemove(); // parent resets valueUrl and form (wl_logo/wl_logoFile)
  };

  const openPicker = () => fileInputRef.current?.click();

  return (
    <div className={styles.settingsLogoUpload}>
      <div
        className={clsx({
          [styles.settingsLogoPreviewEmpty]: valueUrl,
          [styles.settingsLogoPreview]: !valueUrl,
          [styles.settingsLogoPreviewDisabled]: disabled,
        })}
        onClick={!disabled ? openPicker : undefined}
        role={!disabled ? "button" : undefined}
        tabIndex={!disabled ? 0 : undefined}
        onKeyDown={
          !disabled
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") openPicker();
              }
            : undefined
        }
        aria-label="Upload Logo"
        aria-disabled={disabled}
      >
        {valueUrl ? (
          <div className={styles.settingsImageContainer}>
            <img
              src={valueUrl}
              alt="Logo"
              className={styles.settingsUploadedImage}
            />
          </div>
        ) : placeholderImageUrl ? (
          <div className={styles.settingsImageContainer}>
            <img
              src={placeholderImageUrl}
              alt=""
              className={styles.settingsUploadedImage}
            />
          </div>
        ) : (
          <span className={styles.settingsLogoPlaceholder}>
            {(placeholderLetter || "I").toUpperCase()}
          </span>
        )}
        <div
          className={clsx(
            styles.settingsUuploadIcon,
            disabled && styles.uploadIconDisabled,
          )}
        >
          <i className={styles.settingsPhotoIcon} aria-hidden="true" />
        </div>
      </div>

      {valueUrl && (
        <Button
          disabled={disabled}
          onClick={handleRemove}
          className={styles.settingsDeleteButton}
          type="button"
          variant="danger-outline"
        >
          Delete Logo
        </Button>
      )}

      <input
        disabled={disabled}
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(",")}
        onChange={handleImageUpload}
        aria-label="Upload logo"
        hidden
      />
    </div>
  );
}
